"use client"

import Image from "next/image"
import { ChevronDown, Loader2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useParams } from "next/navigation"
import { HttpTypes } from "@medusajs/types"
import { addToCart } from "@lib/data/cart"
import { listProducts } from "@lib/data/products"
import { getSizeFromVariant } from "@modules/cart/utils"
import { MYSTERY_BOX_PRODUCTS, type MysteryBoxItem } from "@constants/cart"

type SizeOption = { value: string; label: string }

function SizeDropdown({
  sizes,
  selectedSize,
  onSelect,
  label,
}: {
  sizes: SizeOption[]
  selectedSize: string
  onSelect: (value: string) => void
  label: string
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  const selectedLabel = sizes.find((s) => s.value === selectedSize)?.label

  return (
    <div className="relative flex-1 min-w-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        className="w-full flex items-center justify-between bg-white border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs text-gray-700 outline-none focus-visible:ring-1 focus-visible:ring-brand-navy"
      >
        <span className={selectedLabel ? "text-gray-900" : "text-gray-500"}>
          {selectedLabel ?? "Select Size"}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute z-10 top-full left-0 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1"
        >
          {sizes.map((size) => (
            <button
              key={size.value}
              type="button"
              role="option"
              aria-selected={size.value === selectedSize}
              onClick={() => {
                onSelect(size.value)
                setOpen(false)
              }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors ${
                size.value === selectedSize ? "text-brand-navy font-semibold" : "text-gray-700"
              }`}
            >
              {size.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function MysteryBoxCard({
  item,
  variants,
  countryCode,
}: {
  item: MysteryBoxItem
  variants: HttpTypes.StoreProductVariant[]
  countryCode: string
}) {
  const [selectedSize, setSelectedSize] = useState("")
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Selecting straight from the real variant list (value = variant id) means
  // add-to-cart never has to re-match a display label back to a variant —
  // avoids the label/value drift that broke the "2XL" size previously.
  const sizeOptions: SizeOption[] = variants.map((v) => ({
    value: v.id,
    label: getSizeFromVariant(v),
  }))

  const handleAddToCart = async () => {
    if (!selectedSize) return
    setError(null)
    setAdding(true)
    const result = await addToCart({ variantId: selectedSize, quantity: 1, countryCode })
    setAdding(false)
    if (!result.success) {
      setError(result.error || "Failed to add to cart.")
    }
  }

  return (
    <div className="flex flex-col gap-1 bg-[#F7F7F7] rounded-lg px-3 py-2.5">
      <div className="flex items-center gap-3">
        {/* Product Image */}
        <div className="relative w-14 h-14 flex-shrink-0">
          <Image
            src={item.image}
            alt={item.name}
            fill
            className="object-contain"
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 flex flex-col gap-1.5">
          {item.popular && (
            <span className="inline-block w-fit px-2 py-0.5 text-[10px] font-semibold bg-brand-navy text-white rounded-full">
              Popular
            </span>
          )}

          {/* Name + Price */}
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-normal text-black truncate">
              {item.name}
            </span>
            <span className="text-xs font-medium text-gray-500 flex-shrink-0 whitespace-nowrap">
              (+£{item.price.toFixed(2)})
            </span>
          </div>

          {/* Size Dropdown + Add Button */}
          <div className="flex items-center gap-2">
            <SizeDropdown
              sizes={sizeOptions}
              selectedSize={selectedSize}
              onSelect={setSelectedSize}
              label={`Choose size for ${item.name}`}
            />

            <button
              type="button"
              onClick={handleAddToCart}
              disabled={!selectedSize || adding}
              className="flex-shrink-0 px-3.5 py-1.5 bg-brand-navy text-white text-xs font-semibold rounded-lg hover:bg-brand-primary-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap flex items-center gap-1.5"
            >
              {adding && <Loader2 className="w-3 h-3 animate-spin" />}
              Add to cart
            </button>
          </div>
        </div>
      </div>

      {error && <p className="text-[10px] text-brand-accent pl-[68px]">{error}</p>}
    </div>
  )
}

export default function MysteryBox() {
  const countryCode = (useParams().countryCode as string) ?? "gb"
  const [variants, setVariants] = useState<HttpTypes.StoreProductVariant[]>([])
  const [fetchError, setFetchError] = useState(false)

  useEffect(() => {
    listProducts({ countryCode, queryParams: { handle: "mystery-box" } })
      .then(({ response }) => setVariants(response.products[0]?.variants ?? []))
      .catch(() => setFetchError(true))
  }, [countryCode])

  // The product fetch failing means there's nothing real to sell — hide the
  // upsell rather than show permanently empty size dropdowns.
  if (fetchError) return null

  return (
    <div className="pt-4" aria-labelledby="mystery-box-heading">
      <p
        id="mystery-box-heading"
        className="text-[13px] font-bold text-brand-navy mb-2.5"
      >
        Add a mystery box
      </p>

      <div className="flex flex-col gap-2.5">
        {MYSTERY_BOX_PRODUCTS.map((item) => (
          <MysteryBoxCard
            key={item.id}
            item={item}
            variants={variants}
            countryCode={countryCode}
          />
        ))}
      </div>
    </div>
  )
}
